import tinycolor from 'tinycolor2';

import type { MailParams } from '@webapp/account/src/resources/mailing/mailing';

import { CDN_URL } from '@webapp/common/conf';
import { getColor } from '@webapp/common/lib/ui';

// eslint-disable-next-line import/no-unresolved
import resizer from '!!raw-loader!iframe-resizer/js/iframeResizer.contentWindow.min';

const fontUrl =
    'https://fonts.googleapis.com/css?family=Montserrat:Montserrat:ital,wght@0,100;0,300;0,400;0,500;0,700;1,100;1,300;1,400;1,500;1,700;1,800&display=swap&subset=cyrillic';

const fontFamily = 'Montserrat,sans-serif,Arial';

const mjmlHead = (style: string, preview: boolean): string => `
<head>
  <title></title>
  <!--[if !mso]><!-->
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <!--<![endif]-->
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  ${preview ? '<script>' + resizer + '</script>' : ''}
  <style type="text/css">
    #outlook a {
      padding: 0;
    }
    body {
      margin: 0;
      padding: 0;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    table, td {
      border-collapse: collapse;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    img {
      border: 0;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
      -ms-interpolation-mode: bicubic;
    }
    .wysiwyg img {
        max-width: 100%;
    }

    p {
      display: block;
      margin: 13px 0;
    }
  </style>
  <!--[if mso]>
        <xml>
        <o:OfficeDocumentSettings>
          <o:AllowPNG/>
          <o:PixelsPerInch>96</o:PixelsPerInch>
        </o:OfficeDocumentSettings>
        </xml>
        <![endif]-->
  <!--[if lte mso 11]>
        <style type="text/css">
          .mj-outlook-group-fix { width:100% !important; }
        </style>
        <![endif]-->
  <style type="text/css">
    @media only screen and (min-width:480px) {
      .mj-column-per-100, .mj-column-px-100 {
        width: 100% !important;
        max-width: 100%;
      }
    }
  </style>
  <style type="text/css">
    @media only screen and (max-width:480px) {
      table.mj-full-width-mobile {
        width: 100% !important;
      }
      td.mj-full-width-mobile {
        width: auto !important;
      }
    }
  </style>
  <!--[if !mso]><!-->
  <link href="${fontUrl}" rel="stylesheet" type="text/css">
  <style type="text/css">@import url(${fontUrl});</style>
  <!--<![endif]-->
  <style type="text/css">${style}</style>
</head>
`;

export const mjmlHtml = (body: string, style: string, preview: boolean): string => `
<!doctype html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">

${mjmlHead(style, preview)}

<body style="word-spacing:normal;background-color:#ffffff;">
  <div style="background-color:#ffffff;">
    ${body}
  </div>
</body>
</html>
`;

const outerWrap = (content: string, color: string): string => `
<!-- outer wrap -->
<!--[if mso | IE]><table align="center" border="0" cellpadding="0" cellspacing="0" class="" style="width:960px;" width="960" ><tr><td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;"><![endif]-->
<div style="background:${color};background-color:${color};margin:0px auto;border-radius:20px;max-width:960px;">
  <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:${color};background-color:${color};width:100%;border-radius:20px;">
    <tbody>
      <tr>
        <td style="direction:ltr;font-size:0px;padding:5px;text-align:center;">
          <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><![endif]-->
          ${content}
          <!--[if mso | IE]></td></tr></table></td></tr></table><![endif]-->
        </td>
      </tr>
    </tbody>
  </table>
</div>
<!--[if mso | IE]></td></tr></table><![endif]-->
`;

const contentWrap = (content: string): string => `
<!-- content wrap -->
<!--[if mso | IE]><tr><td class="" width="960px" ><table align="center" border="0" cellpadding="0" cellspacing="0" class="" style="width:880px;" width="880" ><tr><td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;"><![endif]-->
<div style="background:#ffffff;background-color:#ffffff;margin:0px auto;border-radius:20px;max-width:880px;">
    <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:#ffffff;background-color:#ffffff;width:100%;border-radius:20px;">
      <tbody>
        <tr>
          <td style="direction:ltr;font-size:0px;padding:20px 5px;text-align:center;">
            <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><![endif]-->
            <!--[if mso | IE]><tr><td class="" width="880px" ><table align="center" border="0" cellpadding="0" cellspacing="0" class="" style="width:800px;" width="800" ><tr><td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;"><![endif]-->
            ${content}
            <!--[if mso | IE]></td></tr></table></td></tr><![endif]-->
          </td>
        </tr>
      </tbody>
    </table>
</div>
<!--[if mso | IE]></td></tr></table></td></tr><![endif]-->`;

const logoAlign = (logoImage: string, logoPos: string, width: number, height: number): string => `
<!-- img align -->
<!--[if mso | IE]><tr><td class="" width="880px" ><table align="center" border="0" cellpadding="0" cellspacing="0" class="" style="width:800px;" width="800" ><tr><td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;"><![endif]-->
<div style="margin:0px auto;max-width:800px;">
  <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width:100%;">
    <tbody><tr>
        <td style="direction:ltr;font-size:0px;padding:0px;text-align:center;">
          <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><td class="" style="vertical-align:top;width:800px;" ><![endif]-->
          <div class="mj-column-per-100 mj-outlook-group-fix" style="font-size:0px;text-align:left;direction:ltr;display:inline-block;vertical-align:top;width:100%;">
            <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align:top;" width="100%">
              <tbody><tr>
                  <td align="${logoPos}" style="font-size:0px;padding:0px;word-break:break-word;">
                    <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:collapse;border-spacing:0px;">
                      <tbody><tr>
                          <td style="max-width:266px">
                            <img
                                height="auto"
                                width="${width}" height="${height}"
                                src="${CDN_URL + logoImage}"
                                style="border:0;display:block;outline:none;text-decoration:none;height:auto;width:100%;font-size:13px;" width="100" />
                          </td>
                        </tr></tbody>
                    </table>
                  </td>
                </tr></tbody>
            </table>
          </div>
          <!--[if mso | IE]></td></tr></table><![endif]-->
        </td>
      </tr></tbody>
  </table>
</div>
<!--[if mso | IE]></td></tr></table></td></tr><![endif]-->`;

const spacer = `
<!-- spacer -->
<!--[if mso | IE]><tr><td class="" width="960px" ><![endif]-->
<div style="height:20px;line-height:20px;">&#8202;</div>
<!--[if mso | IE]></td></tr><![endif]-->`;

const footer = (color: string): string => `
  <!-- footer -->
  <!--[if mso | IE]><tr><td class="" width="960px" ><table align="center" border="0" cellpadding="0" cellspacing="0" class="" style="width:880px;" width="880" ><tr><td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;"><![endif]-->
  <div style="background:${color};background-color:${color};margin:0px auto;border-radius:4px;max-width:880px;">
    <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:${color};background-color:${color};width:100%;border-radius:4px;">
      <tbody>
        <tr>
          <td style="border:0;direction:ltr;font-size:0px;padding:0px;text-align:center;">
            <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><td class="" style="vertical-align:top;width:880px;" ><![endif]-->
            <div class="mj-column-per-100 mj-outlook-group-fix" style="font-size:0px;text-align:left;direction:ltr;display:inline-block;vertical-align:top;width:100%;">
              <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="vertical-align:top;" width="100%">
                <tbody>
                  <!-- unsibscribe -->
                  <tr>
                    <td align="left" vertical-align="middle" style="font-size:0px;padding:0;padding-bottom:20px;word-break:break-word;">
                      <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:separate;line-height:100%;width:100%;">
                        <tr>
                          <td align="center" bgcolor="transparent" role="presentation" style="border:none;border-radius:3px;cursor:auto;mso-padding-alt: 0;text-align:center;background:transparent;" valign="middle">
                            <a
                              href="{unsubscribe_link}"
                              style="display:inline-block;background:transparent;color:${getColor(
                                  'grey3',
                              )};font-family:${fontFamily};font-size:13px;font-weight:normal;line-height:120%;margin:0;text-decoration:none;text-transform:none;padding: 0px;mso-padding-alt:0px;border-radius:3px;" target="_blank">
                              Отписаться
                            </a>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!--[if mso | IE]></td></tr></table><![endif]-->
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <!--[if mso | IE]></td></tr></table></td></tr></table><![endif]-->`;

// Outlook for Windows doesn't support rem and inherits font-size:0px from the table cell
const wysiwyg = (body: string): string => `
<div class="wysiwyg" style="font-family:${fontFamily};text-align: left;font-size:16px;">
    ${body}
</div>
`;

const replyButton = (buttonText: string, buttonColor: string): string => `
<!--[if mso | IE]>
<table role="presentation" border="0" cellpadding="0" cellspacing="0">
<tr><td class="" style="vertical-align:top;width:100%;max-width:800px;" align="center">
<![endif]-->
<div
    class="mj-column-per-100 mj-outlook-group-fix"
    style="font-size:0px;text-align:left;direction:ltr;display:inline-block;vertical-align:top;width:100%;"
>
    <table
        border="0" cellpadding="0" cellspacing="0"
        role="presentation" style="vertical-align:top;width:100%;" width="100%"
    >
      <tbody><tr>
        <td
            align="center" vertical-align="middle"
            style="font-size:0px;padding:0px;word-break:break-word;"
        >
            <table
                border="0" cellpadding="0" cellspacing="0"
                role="presentation"
                style="border-collapse:separate;line-height:100%;"
            >
                <tr>
                    <td
                        align="center" bgcolor="${buttonColor}"
                        role="presentation"
                        style="border:none;border-radius:100px;cursor:auto;mso-padding-alt:10px 25px;background:${buttonColor};"
                        valign="middle"
                    >
                        <p
                            style="display:inline-block;background:${buttonColor};color:white;font-family:${fontFamily};font-size:13px;font-weight:normal;line-height:120%;margin:0;text-decoration:none;text-transform:none;padding:10px 25px;mso-padding-alt:0px;border-radius:100px;"
                        >
                            <a href="{survey_link}" style="color:white; text-decoration: none;"
                               style="text-decoration:none;">${buttonText}</a>
                        </p>
                    </td>
                </tr>
            </table>
        </td>
      </tr></tbody>
    </table>
    <i id="end"></i>
</div>
<!--[if mso | IE]></td></tr></table><![endif]-->`;

export const mjmlTemplate = (
    { body, buttonColor, buttonText, headerColor, logoImage, logoImageHeight, logoImageWidth, logoPos }: MailParams,
    style = '',
    preview = false,
): string => {
    const logo = logoImage ? logoAlign(logoImage, logoPos, logoImageWidth, logoImageHeight) : '';
    const content = contentWrap(logo + wysiwyg(body) + replyButton(buttonText, buttonColor));
    const footerColor = tinycolor(headerColor).lighten(20).toRgbString();
    const bodyHtml = outerWrap(content + spacer + footer(footerColor), headerColor);

    return mjmlHtml(bodyHtml, style, preview);
};
